import { AccountSearchResponse } from "../interfaces";

/**
 * Caché de sesión de la búsqueda de clientes.
 *
 * Existe para que volver sobre una consulta ya hecha (borrar una letra, cambiar
 * de campo y regresar) no vuelva a tocar el SP de full text. Vive en memoria:
 * se pierde al recargar, y se vacía completa cuando se crea o edita un cliente,
 * porque después de eso cualquier resultado guardado puede estar desactualizado.
 */
export const ACCOUNT_SEARCH_CACHE_MAX_ENTRIES = 50;
export const ACCOUNT_SEARCH_CACHE_TTL_MS = 60 * 1000;

interface CacheEntry {
  value: AccountSearchResponse;
  storedAt: number;
}

const cache = new Map<string, CacheEntry>();

export const getCachedAccountSearch = (
  key: string
): AccountSearchResponse | undefined => {
  if (!key) {
    return undefined;
  }

  const entry = cache.get(key);
  if (!entry) {
    return undefined;
  }

  if (Date.now() - entry.storedAt >= ACCOUNT_SEARCH_CACHE_TTL_MS) {
    cache.delete(key);
    return undefined;
  }

  return { ...entry.value, source: "cache" };
};

export const setCachedAccountSearch = (
  key: string,
  value: AccountSearchResponse
): void => {
  if (!key || value.didError) {
    return;
  }

  // Reinsertar mueve la clave al final, así el descarte es por antigüedad de uso.
  cache.delete(key);
  cache.set(key, { value, storedAt: Date.now() });

  while (cache.size > ACCOUNT_SEARCH_CACHE_MAX_ENTRIES) {
    const oldest = cache.keys().next();
    if (oldest.done) {
      break;
    }
    cache.delete(oldest.value);
  }
};

export const clearAccountSearchCache = (): void => {
  cache.clear();
};

export const accountSearchCacheSize = (): number => cache.size;
